/**
 * VertrauenPage — Trust-/Sicherheits-Seite (DACH-Kaufargument).
 *
 * Hebt DSGVO-Konformität, Datenisolation, Verschlüsselung und Betroffenenrechte hervor und
 * bietet einen AVV-Muster-Download. Genau das bewerten deutsche KMU strenger als Funktionsumfang.
 */
import { ShieldCheck, Lock, Server, Trash2, Download, Users, FileCheck2, ArrowRight, Brain, ChevronDown } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useEffect, useRef } from "react";
import { buildAvvText } from "@/data/avv";
import { company } from "@/data/company";
import { trackEvent } from "@/lib/trackEvent";
import { getVariant } from "@/lib/ab_utils";

const POINTS = [
  { icon: Server, title: "Daten in der EU", text: "Betrieb und Datenhaltung auf EU-Infrastruktur. KI-Verarbeitung erfolgt über Anthropic und Groq (USA) — offen ausgewiesen, mit AVV und EU-Standardvertragsklauseln abgesichert." },
  { icon: Brain, title: "Keine KI-Trainingsnutzung", text: "Deine Inhalte werden nicht zum Training der KI-Modelle verwendet. Die KI-Anbieter verarbeiten ausschließlich in deinem Auftrag (AVV/EU-SCC) — kein Mitlernen, keine Weiterverwendung deiner Texte." },
  { icon: Users, title: "Strikte Mandantentrennung", text: "Jeder Kunde hat einen eigenen, isolierten Arbeitsbereich. Keine Vermischung von Inhalten, Kanälen oder Tokens." },
  { icon: Lock, title: "Verschlüsselte Zugangs-Tokens", text: "OAuth-/Kanal-Tokens werden verschlüsselt gespeichert (at rest). Zugriff nur authentifiziert (JWT) und rollenbasiert." },
  { icon: Trash2, title: "Betroffenenrechte per Klick", text: "Datenexport (Art. 20) und vollständige Löschung (Art. 17) jederzeit selbst auslösbar — in den Einstellungen." },
  { icon: FileCheck2, title: "AVV inklusive", text: "Auftragsverarbeitungsvertrag nach Art. 28 DSGVO als Vorlage zum Download — für deine eigene Compliance." },
  { icon: ShieldCheck, title: "Made for Germany", text: "Deutsche Oberfläche, deutsches Recht, Impressum & Datenschutz an Bord. Gebaut für Unternehmen in Deutschland — vom Solo-Business bis zum Team." },
];

// Die 10 wichtigsten Vorverkaufs-Einwände (Coaches/Träger/Bildungsanbieter, DACH).
const OBJECTIONS = [
  { q: "Werden meine Inhalte oder Teilnehmerdaten zum KI-Training genutzt?",
    a: "Nein. Deine Inhalte werden nicht zum Training der KI-Modelle verwendet. Die KI-Anbieter verarbeiten ausschließlich in deinem Auftrag (Art. 28 DSGVO, EU-Standardvertragsklauseln) — kein Mitlernen, keine Weiterverwendung." },
  { q: "Setzt ihr chinesische KI ein?",
    a: "Für deine Inhalte nicht. Die Content-Verarbeitung läuft über US-Anbieter (Anthropic, Groq), offen ausgewiesen und mit AVV/EU-SCC abgesichert. KI-Modelle ohne diese Garantien kommen in datenführenden Pfaden nicht zum Einsatz." },
  { q: "Ist das wirklich DSGVO-konform?",
    a: "Betrieb und Datenhaltung in der EU, AVV-Muster nach Art. 28, Betroffenenrechte (Export/Löschung) per Klick, offen ausgewiesene Subprozessoren. Ehrlich gesagt: eine EU-App mit offen ausgewiesenen US-KI-Subprozessoren — keine EU-only-Fiktion." },
  { q: "Brauche ich Technik-Wissen oder einen Redaktionsplan?",
    a: "Nein. Die KI erstellt deinen kompletten Monatsplan auf einen Klick, Kanäle verbindest du per Klick. Kein leeres Blatt, keine Tool-Kette." },
  { q: "Binde ich mich? Wie kündige ich?",
    a: "Du testest 14 Tage kostenlos und ohne Kreditkarte. Danach: Monatsabo monatlich kündbar (keine Mindestlaufzeit), Jahresabo mit 12 Monaten Laufzeit (dafür günstiger). Keine versteckten Fristen." },
  { q: "Komme ich wieder raus — oder bin ich eingesperrt (Lock-in)?",
    a: "Deine Inhalte gehören dir. Du kannst sie jederzeit per Klick exportieren und Förderfuchs ohne Datenverlust wieder verlassen." },
  { q: "Postet die KI automatisch irgendetwas in meinem Namen?",
    a: "Nur, was du freigibst. Beiträge entstehen als Entwurf; nichts geht ungeprüft live. Du behältst die Kontrolle über jede Veröffentlichung." },
  { q: "Wer kann meine Daten sehen?",
    a: "Strikte Mandantentrennung: jeder Kunde hat einen eigenen, isolierten Arbeitsbereich. Zugangs-Tokens werden verschlüsselt gespeichert, Zugriff nur authentifiziert (JWT) und rollenbasiert." },
  { q: "Kann ich risikofrei testen?",
    a: "Ja. 14 Tage kostenlos, ohne Kreditkarte, ohne automatische Abbuchung. Nach der Testphase wählst du aktiv deinen Plan — oder eben nicht." },
  { q: "Was, wenn ich Hilfe brauche?",
    a: "Es gibt eine geführte Einrichtung, ein deutschsprachiges Hilfe-Center und Support. Du startest nicht allein." },
];

export default function VertrauenPage() {
  const ab05Variant = useRef("A");

  useEffect(() => {
    ab05Variant.current = getVariant("AB-05", { trackFn: trackEvent });
    trackEvent("vertrauen_page_viewed", {
      referrer: document.referrer || "",
      ab05_variant: ab05Variant.current,
    });
  }, []);

  const downloadAvv = () => {
    const text = buildAvvText();
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "AVV-Muster-Foerderfuchs.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck size={28} className="text-fox-fire" />
        <h1 className="text-3xl font-bold text-stone-900">Datenschutz & Vertrauen</h1>
      </div>
      <p className="text-stone-500 mb-8 max-w-2xl">
        Wer Kundendaten und Markenauftritt einer KI anvertraut, braucht Gewissheit — Datenschutz
        ist hier kein Papierkram, sondern Voraussetzung für das Vertrauen deiner Kundschaft. Deshalb ist DSGVO-Konformität hier von Grund auf
        eingebaut, nicht nachträglich drangebaut.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        {POINTS.map((p, i) => {
          const Icon = p.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-stone-200 p-5 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                <Icon size={20} className="text-fox-fire" />
              </div>
              <div>
                <div className="font-semibold text-stone-800">{p.title}</div>
                <div className="text-sm text-stone-500 mt-1">{p.text}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Einwandbehandlung: 10 häufigste Fragen vor dem Start ──────────────── */}
      <div className="mb-10">
        <h2 className="text-xl font-bold text-stone-900 mb-4">Häufige Fragen vor dem Start</h2>
        <div className="space-y-2.5">
          {OBJECTIONS.map((o, i) => (
            <details key={i} className="group bg-white rounded-xl border border-stone-200 p-4">
              <summary className="flex items-center justify-between gap-3 cursor-pointer list-none font-medium text-stone-800">
                <span>{o.q}</span>
                <ChevronDown size={18} className="text-stone-400 group-open:rotate-180 transition-transform flex-shrink-0" />
              </summary>
              <p className="text-sm text-stone-500 mt-2 leading-relaxed">{o.a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="bg-stone-50 rounded-xl border border-stone-200 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="font-semibold text-stone-800">Auftragsverarbeitungsvertrag (AVV)</div>
          <div className="text-sm text-stone-500 mt-1">
            Muster nach Art. 28 DSGVO mit unseren Anbieterdaten ({company.legalName}). Vor Verwendung prüfen.
          </div>
        </div>
        <button
          onClick={downloadAvv}
          data-testid="avv-download-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-fox-fire text-white font-medium hover:bg-orange-600 whitespace-nowrap"
        >
          <Download size={18} /> AVV herunterladen
        </button>
      </div>

      <div className="mt-8 rounded-xl bg-orange-50 border border-orange-100 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="font-semibold text-stone-800">Bereit loszulegen?</div>
          <div className="text-sm text-stone-500 mt-1">
            14 Tage kostenlos testen — ohne Kreditkarte, ohne Risiko.
          </div>
        </div>
        <NavLink
          to={ab05Variant.current === "B" ? "/login?tab=register" : "/pricing"}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-fox-fire text-white font-medium hover:bg-orange-600 whitespace-nowrap"
          onClick={() => trackEvent("vertrauen_cta_clicked", {
            variant: ab05Variant.current,
            destination: ab05Variant.current === "B" ? "register" : "pricing",
          })}
        >
          {ab05Variant.current === "B"
            ? <>Kostenlos starten — ohne Kreditkarte <ArrowRight size={16} /></>
            : <>Pakete ansehen <ArrowRight size={16} /></>}
        </NavLink>
      </div>

      <p className="text-xs text-stone-400 mt-4">
        Hinweis: Diese Seite und das AVV-Muster dienen der Information und stellen keine Rechtsberatung dar.
      </p>
    </div>
  );
}
